import React, { useState } from 'react';
import { SearchIcon, PlusIcon, CalendarIcon, CheckCircleIcon, ClockIcon, XIcon, AlertTriangleIcon, BellIcon, CheckIcon } from 'lucide-react';
export function ContractMilestones({
  locale
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [formData, setFormData] = useState({
    contractId: '',
    title: '',
    description: '',
    dueDate: '',
    completionDate: '',
    status: 'Pending',
    reminderDays: '7',
    notifyUsers: []
  });
  // Sample milestones data
  const milestones = [{
    id: 'MLS-2023-001',
    contractId: 'CTR-2023-002',
    contractName: 'IT Support Services',
    vendor: 'TechPro Solutions',
    title: 'Initial Setup Complete',
    description: 'Complete initial system setup and configuration',
    dueDate: '2023-03-15',
    completionDate: '2023-03-12',
    status: 'Completed',
    reminderDays: 7,
    notifyUsers: ['user1@example.com', 'user2@example.com']
  }, {
    id: 'MLS-2023-002',
    contractId: 'CTR-2023-002',
    contractName: 'IT Support Services',
    vendor: 'TechPro Solutions',
    title: 'First Quarterly Review',
    description: 'Conduct first quarterly service review meeting',
    dueDate: '2023-05-01',
    completionDate: '2023-05-03',
    status: 'Completed',
    reminderDays: 7,
    notifyUsers: ['user1@example.com', 'user3@example.com']
  }, {
    id: 'MLS-2023-003',
    contractId: 'CTR-2023-002',
    contractName: 'IT Support Services',
    vendor: 'TechPro Solutions',
    title: 'Second Quarterly Review',
    description: 'Conduct second quarterly service review meeting',
    dueDate: '2023-08-01',
    completionDate: '',
    status: 'Upcoming',
    reminderDays: 14,
    notifyUsers: ['user1@example.com', 'user3@example.com']
  }, {
    id: 'MLS-2023-004',
    contractId: 'CTR-2023-001',
    contractName: 'Office Lease Agreement',
    vendor: 'Skyline Properties Inc.',
    title: 'Annual Inspection',
    description: 'Complete annual property inspection',
    dueDate: '2023-12-15',
    completionDate: '',
    status: 'Upcoming',
    reminderDays: 30,
    notifyUsers: ['user4@example.com']
  }, {
    id: 'MLS-2023-005',
    contractId: 'CTR-2023-006',
    contractName: 'Consulting Services',
    vendor: 'Business Consultants Ltd.',
    title: 'Phase 1 Deliverable',
    description: 'Submit initial assessment report',
    dueDate: '2023-05-30',
    completionDate: '',
    status: 'Overdue',
    reminderDays: 7,
    notifyUsers: ['user1@example.com', 'user5@example.com']
  }, {
    id: 'MLS-2023-006',
    contractId: 'CTR-2023-010',
    contractName: 'Cloud Infrastructure',
    vendor: 'AWS',
    title: 'Migration Complete',
    description: 'Complete migration to new cloud infrastructure',
    dueDate: '2023-09-30',
    completionDate: '',
    status: 'At Risk',
    reminderDays: 14,
    notifyUsers: ['user6@example.com', 'user7@example.com']
  }];
  // Filter milestones based on search term
  const filteredMilestones = milestones.filter(milestone => milestone.id.toLowerCase().includes(searchTerm.toLowerCase()) || milestone.contractId.toLowerCase().includes(searchTerm.toLowerCase()) || milestone.contractName.toLowerCase().includes(searchTerm.toLowerCase()) || milestone.vendor.toLowerCase().includes(searchTerm.toLowerCase()) || milestone.title.toLowerCase().includes(searchTerm.toLowerCase()) || milestone.description.toLowerCase().includes(searchTerm.toLowerCase()));
  // Get status badge color
  const getStatusColor = status => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Upcoming':
        return 'bg-blue-100 text-blue-800';
      case 'Overdue':
        return 'bg-red-100 text-red-800';
      case 'At Risk':
        return 'bg-yellow-100 text-yellow-800';
      case 'Pending':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Get status icon
  const getStatusIcon = status => {
    switch (status) {
      case 'Completed':
        return CheckCircleIcon;
      case 'Upcoming':
        return ClockIcon;
      case 'Overdue':
        return XIcon;
      case 'At Risk':
        return AlertTriangleIcon;
      case 'Pending':
        return ClockIcon;
      default:
        return ClockIcon;
    }
  };
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    // In a real app, this would save the milestone to the database
    console.log('Saving milestone:', formData);
    // Show success message
    alert(`Milestone saved successfully!`);
    // Reset form and view
    setShowForm(false);
    setSelectedMilestone(null);
  };
  const handleAddNew = () => {
    setFormData({
      contractId: '',
      title: '',
      description: '',
      dueDate: '',
      completionDate: '',
      status: 'Pending',
      reminderDays: '7',
      notifyUsers: []
    });
    setSelectedMilestone(null);
    setShowForm(true);
  };
  const handleEdit = milestone => {
    setFormData({
      contractId: milestone.contractId,
      title: milestone.title,
      description: milestone.description,
      dueDate: milestone.dueDate,
      completionDate: milestone.completionDate,
      status: milestone.status,
      reminderDays: milestone.reminderDays.toString(),
      notifyUsers: milestone.notifyUsers
    });
    setSelectedMilestone(milestone);
    setShowForm(true);
  };
  const handleMarkComplete = id => {
    // In a real app, this would update the milestone status in the database
    console.log('Marking milestone complete:', id);
    alert(`Milestone ${id} marked as complete`);
  };
  return <div data-id="element-2746">
      <div className="mb-8 flex justify-between items-center" data-id="element-2747">
        <div data-id="element-2748">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-2749">
            Contract Milestones
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-2750">
            Track important dates and deliverables for your contracts
          </p>
        </div>
        <button onClick={handleAddNew} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none" data-id="element-2751">
          <PlusIcon className="h-4 w-4 mr-2" data-id="element-2752" />
          Add Milestone
        </button>
      </div>
      {showForm ? <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-2753">
          <form onSubmit={handleSubmit} className="p-6" data-id="element-2754">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-2755">
              {/* Contract Selection */}
              <div className="sm:col-span-3" data-id="element-2756">
                <label htmlFor="contractId" className="block text-sm font-medium text-gray-700" data-id="element-2757">
                  Contract
                </label>
                <div className="mt-1" data-id="element-2758">
                  <select id="contractId" name="contractId" value={formData.contractId} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2759">
                    <option value="" data-id="element-2760">Select a contract</option>
                    <option value="CTR-2023-001" data-id="element-2761">
                      CTR-2023-001 - Office Lease Agreement
                    </option>
                    <option value="CTR-2023-002" data-id="element-2762">
                      CTR-2023-002 - IT Support Services
                    </option>
                    <option value="CTR-2023-006" data-id="element-2763">
                      CTR-2023-006 - Consulting Services
                    </option>
                    <option value="CTR-2023-008" data-id="element-2764">
                      CTR-2023-008 - Security Services
                    </option>
                    <option value="CTR-2023-010" data-id="element-2765">
                      CTR-2023-010 - Cloud Infrastructure
                    </option>
                  </select>
                </div>
              </div>
              {/* Milestone Title */}
              <div className="sm:col-span-3" data-id="element-2766">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700" data-id="element-2767">
                  Milestone Title
                </label>
                <div className="mt-1" data-id="element-2768">
                  <input type="text" name="title" id="title" value={formData.title} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2769" />
                </div>
              </div>
              {/* Description */}
              <div className="sm:col-span-6" data-id="element-2770">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-2771">
                  Description
                </label>
                <div className="mt-1" data-id="element-2772">
                  <textarea id="description" name="description" rows={2} value={formData.description} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2773" />
                </div>
              </div>
              {/* Due Date */}
              <div className="sm:col-span-3" data-id="element-2774">
                <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700" data-id="element-2775">
                  Due Date
                </label>
                <div className="mt-1" data-id="element-2776">
                  <input type="date" name="dueDate" id="dueDate" value={formData.dueDate} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2777" />
                </div>
              </div>
              {/* Completion Date */}
              <div className="sm:col-span-3" data-id="element-2778">
                <label htmlFor="completionDate" className="block text-sm font-medium text-gray-700" data-id="element-2779">
                  Completion Date
                </label>
                <div className="mt-1" data-id="element-2780">
                  <input type="date" name="completionDate" id="completionDate" value={formData.completionDate} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2781" />
                </div>
              </div>
              {/* Status */}
              <div className="sm:col-span-3" data-id="element-2782">
                <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-2783">
                  Status
                </label>
                <div className="mt-1" data-id="element-2784">
                  <select id="status" name="status" value={formData.status} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2785">
                    <option value="Pending" data-id="element-2786">Pending</option>
                    <option value="Upcoming" data-id="element-2787">Upcoming</option>
                    <option value="At Risk" data-id="element-2788">At Risk</option>
                    <option value="Overdue" data-id="element-2789">Overdue</option>
                    <option value="Completed" data-id="element-2790">Completed</option>
                  </select>
                </div>
              </div>
              {/* Reminder Days */}
              <div className="sm:col-span-3" data-id="element-2791">
                <label htmlFor="reminderDays" className="block text-sm font-medium text-gray-700" data-id="element-2792">
                  Send Reminder (days before due date)
                </label>
                <div className="mt-1" data-id="element-2793">
                  <select id="reminderDays" name="reminderDays" value={formData.reminderDays} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2794">
                    <option value="1" data-id="element-2795">1 day</option>
                    <option value="3" data-id="element-2796">3 days</option>
                    <option value="7" data-id="element-2797">1 week</option>
                    <option value="14" data-id="element-2798">2 weeks</option>
                    <option value="30" data-id="element-2799">1 month</option>
                    <option value="60" data-id="element-2800">2 months</option>
                    <option value="90" data-id="element-2801">3 months</option>
                  </select>
                </div>
              </div>
              {/* Notify Users */}
              <div className="sm:col-span-6" data-id="element-2802">
                <label htmlFor="notifyUsers" className="block text-sm font-medium text-gray-700" data-id="element-2803">
                  Notify Users (comma separated emails)
                </label>
                <div className="mt-1" data-id="element-2804">
                  <input type="text" name="notifyUsers" id="notifyUsers" value={formData.notifyUsers.join(', ')} onChange={e => {
                const emails = e.target.value.split(',').map(email => email.trim()).filter(email => email);
                setFormData({
                  ...formData,
                  notifyUsers: emails
                });
              }} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="user1@example.com, user2@example.com" data-id="element-2805" />
                </div>
              </div>
            </div>
            <div className="pt-5 mt-6 border-t border-gray-200" data-id="element-2806">
              <div className="flex justify-end" data-id="element-2807">
                <button type="button" onClick={() => setShowForm(false)} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none" data-id="element-2808">
                  Cancel
                </button>
                <button type="submit" className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none" data-id="element-2809">
                  {selectedMilestone ? 'Update Milestone' : 'Create Milestone'}
                </button>
              </div>
            </div>
          </form>
        </div> : <>
          {/* Search */}
          <div className="mb-6" data-id="element-2810">
            <div className="max-w-md" data-id="element-2811">
              <div className="relative rounded-md shadow-sm" data-id="element-2812">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2813">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-2814" />
                </div>
                <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search milestones..." data-id="element-2815" />
              </div>
            </div>
          </div>
          {/* Milestones Stats */}
          <div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-2816">
            <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-2817">
              <div className="px-4 py-5 sm:p-6" data-id="element-2818">
                <div className="flex items-center" data-id="element-2819">
                  <div className="flex-shrink-0 bg-green-100 rounded-md p-3" data-id="element-2820">
                    <CheckCircleIcon className="h-6 w-6 text-green-600" data-id="element-2821" />
                  </div>
                  <div className="ml-5 w-0 flex-1" data-id="element-2822">
                    <dl data-id="element-2823">
                      <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-2824">
                        Completed
                      </dt>
                      <dd data-id="element-2825">
                        <div className="text-lg font-medium text-gray-900" data-id="element-2826">
                          {milestones.filter(m => m.status === 'Completed').length}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-2827">
              <div className="px-4 py-5 sm:p-6" data-id="element-2828">
                <div className="flex items-center" data-id="element-2829">
                  <div className="flex-shrink-0 bg-blue-100 rounded-md p-3" data-id="element-2830">
                    <ClockIcon className="h-6 w-6 text-blue-600" data-id="element-2831" />
                  </div>
                  <div className="ml-5 w-0 flex-1" data-id="element-2832">
                    <dl data-id="element-2833">
                      <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-2834">
                        Upcoming
                      </dt>
                      <dd data-id="element-2835">
                        <div className="text-lg font-medium text-gray-900" data-id="element-2836">
                          {milestones.filter(m => m.status === 'Upcoming').length}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-2837">
              <div className="px-4 py-5 sm:p-6" data-id="element-2838">
                <div className="flex items-center" data-id="element-2839">
                  <div className="flex-shrink-0 bg-yellow-100 rounded-md p-3" data-id="element-2840">
                    <AlertTriangleIcon className="h-6 w-6 text-yellow-600" data-id="element-2841" />
                  </div>
                  <div className="ml-5 w-0 flex-1" data-id="element-2842">
                    <dl data-id="element-2843">
                      <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-2844">
                        At Risk
                      </dt>
                      <dd data-id="element-2845">
                        <div className="text-lg font-medium text-gray-900" data-id="element-2846">
                          {milestones.filter(m => m.status === 'At Risk').length}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-2847">
              <div className="px-4 py-5 sm:p-6" data-id="element-2848">
                <div className="flex items-center" data-id="element-2849">
                  <div className="flex-shrink-0 bg-red-100 rounded-md p-3" data-id="element-2850">
                    <XIcon className="h-6 w-6 text-red-600" data-id="element-2851" />
                  </div>
                  <div className="ml-5 w-0 flex-1" data-id="element-2852">
                    <dl data-id="element-2853">
                      <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-2854">
                        Overdue
                      </dt>
                      <dd data-id="element-2855">
                        <div className="text-lg font-medium text-gray-900" data-id="element-2856">
                          {milestones.filter(m => m.status === 'Overdue').length}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Milestones Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-2857">
            <div className="overflow-x-auto" data-id="element-2858">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-2859">
                <thead className="bg-gray-50" data-id="element-2860">
                  <tr data-id="element-2861">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2862">
                      Milestone
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2863">
                      Contract
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2864">
                      Description
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2865">
                      Dates
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2866">
                      Reminder
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2867">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2868">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-2869">
                  {filteredMilestones.length === 0 ? <tr data-id="element-2870">
                      <td colSpan={7} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-2871">
                        No milestones found matching your criteria.
                      </td>
                    </tr> : filteredMilestones.map(milestone => {
                const StatusIcon = getStatusIcon(milestone.status);
                return <tr key={milestone.id} className="hover:bg-gray-50" data-id="element-2872">
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-2873">
                            <div className="flex items-center" data-id="element-2874">
                              <div data-id="element-2875">
                                <div className="text-sm font-medium text-indigo-600" data-id="element-2876">
                                  {milestone.id}
                                </div>
                                <div className="text-sm text-gray-500" data-id="element-2877">
                                  {milestone.title}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-2878">
                            <div className="text-sm text-gray-900" data-id="element-2879">
                              {milestone.contractId}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-2880">
                              {milestone.contractName}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-2881">
                              {milestone.vendor}
                            </div>
                          </td>
                          <td className="px-6 py-4" data-id="element-2882">
                            <div className="text-sm text-gray-900 max-w-xs truncate" data-id="element-2883">
                              {milestone.description}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2884">
                            <div className="flex items-center" data-id="element-2885">
                              <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-2886" />
                              <span data-id="element-2887">Due: {milestone.dueDate}</span>
                            </div>
                            {milestone.completionDate && <div className="flex items-center mt-1" data-id="element-2888">
                                <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-2889" />
                                <span data-id="element-2890">
                                  Completed: {milestone.completionDate}
                                </span>
                              </div>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2891">
                            <div className="flex items-center" data-id="element-2892">
                              <BellIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-2893" />
                              <span data-id="element-2894">{milestone.reminderDays} days before</span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-2895">
                              {milestone.notifyUsers.length} users notified
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-2896">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(milestone.status)}`} data-id="element-2897">
                              <StatusIcon className="h-3 w-3 mr-1" data-id="element-2898" />
                              {milestone.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2899">
                            <div className="flex justify-end space-x-2" data-id="element-2900">
                              <button onClick={() => handleEdit(milestone)} className="text-indigo-600 hover:text-indigo-900" data-id="element-2901">
                                Edit
                              </button>
                              {milestone.status !== 'Completed' && <button onClick={() => handleMarkComplete(milestone.id)} className="text-green-600 hover:text-green-900" title="Mark as Complete" data-id="element-2902">
                                  <CheckIcon className="h-4 w-4" data-id="element-2903" />
                                </button>}
                            </div>
                          </td>
                        </tr>;
              })}
                </tbody>
              </table>
            </div>
          </div>
        </>}
    </div>;
}